import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from "~/hooks/redux";
import { getAuthUser } from "~/store/actions";
import { initTimezone } from '~/helpers/dateHelper';
import { NetworkErrorException } from '~/helpers/errorHelper';
import Error from '~/pages/Error';

const AuthContext = React.createContext();

const AuthProvider = props => {

  const dispatch = useAppDispatch();

  // warning
  // useAppSelector() will cause a re-render whenever the result of its callback function changes
  // for example: const { user, isAuth } = useAppSelector(state => state.Auth.Login);
  // you might expect to get a re-render only when 'user' or 'isAuth' change
  // in fact you will get a re-render when anything inside 'state.Auth.Login' changes, not just 'user' and 'isAuth'
  // so if that object contains other properties, unwanted re-renders will happen when they change
  // that is why, instead of destructuring, we target specific state properties one by one
  // https://9oelm.github.io/2020-09-13--How-to-make-useSelector-not-a-disaster/#function-component
  const user = useAppSelector(state => state.Auth.Login.user);
  const isAuth = useAppSelector(state => state.Auth.Login.isAuth);
  const userError = useAppSelector(state => state.Auth.Login.userError);

  const refreshAuthUser = useCallback(() => {
    dispatch(getAuthUser());
  }, [dispatch]);

  const isLoggedIn = useCallback(() => !!user, [user]);

  const isImpersonation = useCallback(() => user.isImpersonated, [user]);

  const getUserRoleClass = useCallback(() => user.userRoleId, [user]);

  const value = useMemo(
    () => ({ user, isLoggedIn, isImpersonation, refreshAuthUser, getUserRoleClass }),
    [user, isLoggedIn, isImpersonation, refreshAuthUser, getUserRoleClass]
  );

  useEffect(() => {
    if (isAuth === null) {
      refreshAuthUser();
    }
  }, [isAuth, refreshAuthUser]);

  if (!!user) {
    initTimezone(user.timezone);
  }

  // note: when isAuth === null (/me not yet resolved), we deliberately render children
  // rather than blocking with a <Preloader />. this allows SubscriptionContext — a child in
  // the provider stack — to mount immediately and dispatch its own /me/subscription request
  // in parallel with our /me request, instead of waiting for auth to resolve first (sequential waterfall).
  // SubscriptionContext owns the single loading gate: it waits until both isAuth and dealerStore
  // are resolved before rendering the app, so the user still sees exactly one preloader on startup.
  if (userError instanceof NetworkErrorException) {
    return <Error error={userError} />
  }
  return <AuthContext.Provider value={value} {...props} />
}

// helper hook that makes context data available
export const useAuth = () => React.useContext(AuthContext);

export default AuthProvider;